/**
 * IconPicker - Simple emoji icon selector
 *
 * A dropdown that lets users pick an emoji icon for widgets
 * (primarily buttons). Shows a grid of commonly used icons.
 * Click the current icon to open, click a new one to select it.
 */

import { useState, useRef, useEffect } from 'react';

// Common icons organized for quick selection
const COMMON_ICONS = [
  // Navigation & actions
  '←', '→', '↑', '↓', '✓', '×', '▶', '⏸',
  // Objects
  '🛒', '📋', '📊', '📝', '🔍', '⚙️', '🎤', '💬',
  // Status & indicators
  '❤️', '⭐', '🔔', '📌', '🏠', '👤', '📱', '💡',
];

interface IconPickerProps {
  /** Currently selected icon (emoji string or undefined) */
  value?: string;
  /** Called when user picks a new icon */
  onChange: (icon: string | undefined) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as HTMLElement)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={pickerRef} onClick={(e) => e.stopPropagation()}>
      {/* Trigger button - shows current icon or a "+" */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="nodrag nopan flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:border-violet-300 hover:bg-violet-50 transition-colors"
      >
        <span className="text-sm">{value || '➕'}</span>
        <span className="text-gray-500">{value ? 'Icon' : 'Add Icon'}</span>
      </button>

      {/* Dropdown grid of icons */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 p-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 w-[200px]">
          <div className="grid grid-cols-8 gap-0.5">
            {COMMON_ICONS.map((icon) => (
              <button
                key={icon}
                type="button"
                onClick={() => {
                  onChange(icon);
                  setIsOpen(false);
                }}
                className={`
                  p-1.5 rounded text-sm hover:bg-violet-50 transition-colors text-center
                  ${value === icon ? 'bg-violet-100 ring-1 ring-violet-300' : ''}
                `}
              >
                {icon}
              </button>
            ))}
          </div>

          {/* Remove icon option (only show if an icon is currently set) */}
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
                setIsOpen(false);
              }}
              className="w-full mt-1.5 px-2 py-1 text-[10px] text-red-500 hover:bg-red-50 rounded transition-colors text-center border-t border-gray-100"
            >
              Remove Icon
            </button>
          )}
        </div>
      )}
    </div>
  );
}
